import {useEffect, useState} from 'react';

import {Grid, Typography} from '@mui/material';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';

import Button from '../../components/form/buttons/Button/Button';
import Link from '../../components/link/Link';
import AppSnackbars from '../../components/snackbar/AppSnackbars';
import Spinner from '../../components/spinner/Spinner';
import {APP_ROUTES, FONT_FAMILIES, SPINNER_TYPES} from '../../const';
import {useDefaultFavicon} from '../../hooks/misc/useDefaultFavicon';
import {useDefaultPageTitle} from '../../hooks/misc/useDefaultPageTitle';
import useLoading from '../../hooks/providers/useLoading';
import useProjects from '../../hooks/providers/useProjects';
import defaultAnimation from '../../lotties/spinner/greySpinnerAnimation.json';
import {capitalizeWord} from '../../utils';

const ConnectorAuthenticationCallbackPage = () => {
  const {
    loginToConnector,
    isGeneratingConnectorToken,
    setIsGeneratingConnectorToken,
    oauthError: error,
    setOauthError: setError,
    getConnectors,
    checkConnectorConnection,
    setIsSuccessfullyLoggedInToConnector
  } = useProjects();
  const {setSpinnerType, spinnerText, setSpinnerText, setShouldDisplayNonBrandedSpinner} = useLoading();

  const [searchParams] = useSearchParams();
  const [appBaseUrl, setAppBaseUrl] = useState('');

  const navigate = useNavigate();
  const location = useLocation();

  useDefaultFavicon();
  useDefaultPageTitle();

  // TODO: replace this, not a proper solution.
  const connectorId = location.pathname.replace('-oauth-callback', '').slice(1);

  const capitalizedConnectorName = capitalizeWord(connectorId);

  const authenticationCode = searchParams.get('code');
  const oauthState = searchParams.get('state');
  const isRedirectedAfterSuccessfulProcess = searchParams.get('success');

  const handlePostSuccessfulLogin = async () => {
    checkConnectorConnection(connectorId);
    await getConnectors();

    setIsGeneratingConnectorToken(false);
    setIsSuccessfullyLoggedInToConnector(true);

    navigate(APP_ROUTES.connections, {
      state: {
        isProjectCreationModalOpen: true,
        connectorId
      }
    });
  };

  const handleOAuthCodeValidation = async () => {
    const res = await loginToConnector(connectorId, {
      code: authenticationCode,
      state: oauthState
    });

    if (!res.success) {
      setAppBaseUrl(res.data.redirect_uri);

      setError(`Une erreur est survenue lors de la récupération des informations depuis ${capitalizedConnectorName}.`);
    } else {
      window.location.href = `${res.data.redirect_uri}?success=true&connector_id=${res.data.connector}`;
    }

    setIsGeneratingConnectorToken(false);
  };

  useEffect(() => {
    (async () => {
      if ((!location.search || !authenticationCode) && !isRedirectedAfterSuccessfulProcess) {
        setError('Votre requête est invalide.');
        return;
      }

      setSpinnerType(SPINNER_TYPES.DEFAULT);
      if (!isGeneratingConnectorToken) {
        setShouldDisplayNonBrandedSpinner(true);
        setSpinnerText(`Récupération des informations depuis ${capitalizedConnectorName}`);
        setIsGeneratingConnectorToken(true);

        if (isRedirectedAfterSuccessfulProcess) {
          handlePostSuccessfulLogin();
        } else {
          handleOAuthCodeValidation();
        }
      }
    })();
  }, []);

  useEffect(() => {
    if (!authenticationCode && !isRedirectedAfterSuccessfulProcess) {
      navigate(APP_ROUTES.connections);
    }
  }, []);

  if (error && !isGeneratingConnectorToken) {
    return (
      <Grid container flex={1} height="100vh" alignItems="center" justifyContent="center" flexDirection="column" spacing={2}>
        <AppSnackbars />
        <Grid item>
          <Typography textAlign="center" fontSize={24} fontFamily={FONT_FAMILIES.BODY} fontWeight="bold">
            Oups !
          </Typography>
          <Typography textAlign="center" fontSize={18} fontFamily={FONT_FAMILIES.BODY} fontWeight="bold">
            {error}
          </Typography>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            sx={{
              background: '#adadad',
              '&:hover': {background: '#D3D3D3'}
            }}
          >
            <Link style={{color: 'white'}} href={`${appBaseUrl}/${APP_ROUTES.connections}`}>
              Retourner à mes connexions
            </Link>
          </Button>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container height="100vh" width="100%">
      <Spinner text={spinnerText} overrideAnimation={defaultAnimation} isLoading={isGeneratingConnectorToken} />
    </Grid>
  );
};

export default ConnectorAuthenticationCallbackPage;
